import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Link, NavLink } from 'react-router-dom';
import {
  Search,
  Building2,
  List,
  Mail,
  Clock,
  Flag,
  ArrowLeftRight,
  BarChart2,
  Users,
  LogOut,
  Zap,
  Menu,
  Home,
  LayoutDashboard,
  Tag,
  UserPlus,
  Send,
  ClipboardList,
  Shield,
  Settings as SettingsIcon,
} from 'lucide-react';
import AddContactModal from './AddContactModal';
import NycemLogo from './NycemLogo';

// 256px leaves 228px for the logo inside 14px padding — the brand guide's minimum is 225px.
const SIDEBAR_WIDTH = 256;
const SIDEBAR_LOGO_WIDTH = SIDEBAR_WIDTH - 28;

// ─── Types ────────────────────────────────────────────────────────────────────

interface StoredUser {
  id: string;
  email: string;
  role: 'Administrator' | 'Superuser' | 'User';
  first_name: string;
  last_name: string;
  canSeeRestricted: boolean;
}

interface BadgeCounts {
  pending: number;
  flags: number;
  updateRequests: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getUser = (): StoredUser | null => {
  try {
    return JSON.parse(sessionStorage.getItem('iad2_user') || 'null');
  } catch {
    return null;
  }
};

const getInitials = (user: StoredUser | null): string => {
  if (!user) return '?';
  const f = user.first_name?.[0] ?? '';
  const l = user.last_name?.[0] ?? '';
  return (f + l).toUpperCase() || user.email?.[0]?.toUpperCase() || '?';
};

const isAdminOrSuperuser = (role?: string) =>
  role === 'Administrator' || role === 'Superuser';

const BREADCRUMB_MAP: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/directory': 'Directory',
  '/agencies': 'Agencies',
  '/lists': 'Lists',
  '/flags': 'Raised Flags',
  '/pending': 'Pending Changes',
  '/update-requests': 'Update Requests',
  '/kpi': 'KPI Dashboard',
  '/import': 'Import / Export',
  '/admin/users': 'User Management',
  '/admin/roles': 'Role Management',
  '/admin/audit-log': 'Audit Log',
  '/admin/settings': 'Settings',
  '/capabilities': 'Role Capabilities',
  '/quick-send': 'Quick Send',
};

const getBreadcrumb = (pathname: string): string => {
  if (BREADCRUMB_MAP[pathname]) return BREADCRUMB_MAP[pathname];
  if (pathname.startsWith('/contacts/')) return 'Contact Detail';
  if (pathname.startsWith('/agencies/')) return 'Agency Detail';
  return 'IAD 2.0';
};

// ─── API helper ───────────────────────────────────────────────────────────────

const apiFetch = async (path: string): Promise<any> => {
  const token = sessionStorage.getItem('iad2_token');
  const base =
    process.env.REACT_APP_API_URL ??
    process.env.REACT_APP_API_BASE_URL ??
    'https://yvui056ywi.execute-api.us-east-1.amazonaws.com/prototype';
  const res = await fetch(`${base}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// ─── NavItem component ────────────────────────────────────────────────────────

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  badgeColor?: string;
  onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, badge, badgeColor = 'var(--red)', onClick }) => {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        borderRadius: 6,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 500,
        color: isActive ? 'var(--white)' : 'rgba(255,255,255,0.65)',
        background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
        margin: '1px 0',
        transition: 'background 0.15s, color 0.15s',
        position: 'relative',
      })}
      className="sidebar-item"
    >
      <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {badge != null && badge > 0 && (
        <span
          className="badge"
          style={{
            background: badgeColor,
            color: 'var(--white)',
            fontSize: 12,
            fontWeight: 700,
            padding: '1px 6px',
            borderRadius: 10,
            lineHeight: '16px',
            minWidth: 18,
            textAlign: 'center',
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
};

// ─── Section header ───────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <div
    className="sidebar-section"
    style={{
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.08em',
      color: 'rgba(255,255,255,0.72)',
      textTransform: 'uppercase',
      padding: '16px 14px 4px',
    }}
  >
    {label}
  </div>
);

// ─── Main Layout ──────────────────────────────────────────────────────────────

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [counts, setCounts] = useState<BadgeCounts>({ pending: 0, flags: 0, updateRequests: 0 });
  const [showAddContact, setShowAddContact] = useState(false);

  const user = getUser();
  const initials = getInitials(user);
  const isAdmin = isAdminOrSuperuser(user?.role);
  const isAdministratorOnly = user?.role === 'Administrator';
  const breadcrumb = getBreadcrumb(location.pathname);

  // Responsive
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Keyboard shortcut ⌘K / Ctrl+K → Quick Send
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        navigate('/quick-send');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Fetch badge counts
  const fetchCounts = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [pendingRes, flagsRes, urRes] = await Promise.allSettled([
        apiFetch('/pending-changes?status_id=1&limit=1'),
        apiFetch('/flags?status=Open&limit=1'),
        apiFetch('/update-requests?status_id=1&limit=1'),
      ]);

      setCounts((prev) => {
        const next = { ...prev };
        if (pendingRes.status === 'fulfilled') {
          next.pending = pendingRes.value?.meta?.total ?? 0;
        }
        if (flagsRes.status === 'fulfilled') {
          next.flags = flagsRes.value?.meta?.total ?? 0;
        }
        if (urRes.status === 'fulfilled') {
          next.updateRequests = urRes.value?.meta?.total ?? 0;
        }
        return next;
      });
    } catch {
      // silently fail — badges just stay 0
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    const handler = () => {
      fetchCounts();
      setTimeout(fetchCounts, 600);
    };
    window.addEventListener('iad2:counts-stale', handler);
    return () => window.removeEventListener('iad2:counts-stale', handler);
  }, [fetchCounts]);

  const handleLogout = () => {
    sessionStorage.removeItem('iad2_token');
    sessionStorage.removeItem('iad2_user');
    navigate('/login', { replace: true });
  };

  // ── Sidebar content ──────────────────────────────────────────────────────────

  const sidebarContent = (
    <div
      style={{
        width: isMobile ? 280 : SIDEBAR_WIDTH,
        minWidth: isMobile ? 280 : SIDEBAR_WIDTH,
        height: '100vh',
        background: 'var(--nycem-blue)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Logo */}
      <div style={{ padding: '18px 14px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          type="button"
          aria-label="IAD 2.0 home"
          style={{ display: 'block', background: 'none', border: 'none', padding: 0, marginBottom: 14, cursor: 'pointer', textAlign: 'left' }}
          onClick={() => navigate('/dashboard')}
        >
          <NycemLogo variant="white" width={SIDEBAR_LOGO_WIDTH} />
          <div
            style={{
              marginTop: 10,
              paddingTop: 8,
              borderTop: '1px solid rgba(181,203,231,0.3)',
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--sky)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              lineHeight: 1.3,
            }}
          >
            Interagency Directory
          </div>
        </button>

        {/* Quick Send button */}
        <button
          onClick={() => navigate('/quick-send')}
          style={{
            width: '100%',
            background: 'var(--orange)',
            color: 'var(--white)',
            border: 'none',
            borderRadius: 4,
            padding: '7px 10px',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 6,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Zap size={13} />
            Quick Send
          </span>
          {!isMobile && (
            <span
              className="badge"
              style={{
                background: 'rgba(0,0,0,0.2)',
                color: 'rgba(255,255,255,0.9)',
                fontSize: 12,
                fontWeight: 600,
                padding: '1px 5px',
                borderRadius: 3,
                fontFamily: 'monospace',
                letterSpacing: 0,
              }}
            >
              ⌘K
            </span>
          )}
        </button>

        {/* Quick Actions (Admin/Superuser only) */}
        {isAdmin && (
          <div style={{ marginTop: 12 }}>
            <div style={{
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.08em',
              color: 'rgba(255,255,255,0.72)',
              marginBottom: 4,
              textTransform: 'uppercase',
            }}>
              Quick Actions
            </div>
            {[
              { icon: <UserPlus size={13} />, label: '+ Add Contact', action: () => setShowAddContact(true) },
              { icon: <Building2 size={13} />, label: '+ Add Agency', action: () => navigate('/agencies?create=1') },
              { icon: <List size={13} />, label: '+ New List', action: () => navigate('/lists?create=1') },
              { icon: <Send size={13} />, label: '+ Send Update Request', action: () => navigate('/update-requests?create=1') },
            ].map(({ icon, label, action }) => (
              <button
                key={label}
                onClick={action}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '6px 12px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  borderRadius: 6,
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: 12,
                  cursor: 'pointer',
                  marginBottom: 4,
                  textAlign: 'left',
                }}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '6px 8px' }}>
        <SectionLabel label="Directory" />
        <NavItem to="/dashboard" icon={<LayoutDashboard size={15} />} label="Dashboard" />
        <NavItem to="/directory" icon={<Search size={15} />} label="Directory" />
        <NavItem to="/agencies" icon={<Building2 size={15} />} label="Agencies" />
        <NavItem to="/lists" icon={<List size={15} />} label="Lists" />

        <SectionLabel label="Tools" />
        {isAdmin && (
          <NavItem
            to="/update-requests"
            icon={<Mail size={15} />}
            label="Update Requests"
            badge={counts.updateRequests}
            badgeColor="var(--amber)"
          />
        )}
        {isAdmin && (
          <NavItem
            to="/pending"
            icon={<Clock size={15} />}
            label="Pending Changes"
            badge={counts.pending}
            badgeColor="var(--red)"
          />
        )}
        <NavItem
          to="/flags"
          icon={<Flag size={15} />}
          label="Raised Flags"
          badge={isAdmin ? counts.flags : undefined}
          badgeColor="var(--red)"
        />
        <NavItem to="/import" icon={<ArrowLeftRight size={15} />} label="Import / Export" />
        <NavItem to="/capabilities" icon={<Shield size={15} />} label="Role Capabilities" />

        {isAdmin && (
          <>
            <SectionLabel label="Admin" />
            <NavItem to="/kpi" icon={<BarChart2 size={15} />} label="KPI Dashboard" />
            {isAdministratorOnly && (
              <NavItem to="/admin/users" icon={<Users size={15} />} label="User Management" />
            )}
            {isAdministratorOnly && (
              <NavItem to="/admin/roles" icon={<Tag size={15} />} label="Role Management" />
            )}
            {isAdministratorOnly && (
              <NavItem to="/admin/audit-log" icon={<ClipboardList size={15} />} label="Audit Log" />
            )}
            {isAdministratorOnly && (
              <NavItem to="/admin/settings" icon={<SettingsIcon size={15} />} label="Settings" />
            )}
          </>
        )}
      </nav>

      {/* User footer */}
      <div
        style={{
          padding: '12px 14px 32px',
          marginBottom: 16,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {/* Avatar */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--nycem-blue)',
            border: '1px solid rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--white)',
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--white)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user ? `${user.first_name} ${user.last_name}`.trim() || user.email : 'Unknown'}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.72)', lineHeight: 1.3 }}>
            {user?.role ?? '—'}
          </div>
        </div>
        <button
          onClick={handleLogout}
          title="Sign out"
          style={{
            background: 'none',
            border: 'none',
            padding: 4,
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.72)',
            display: 'flex',
            alignItems: 'center',
            borderRadius: 4,
            flexShrink: 0,
          }}
        >
          <LogOut size={15} />
        </button>
      </div>
    </div>
  );

  // ── Mobile bottom tab bar ─────────────────────────────────────────────────────

  const mobileTabBar = isMobile ? (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 56,
        background: 'var(--white)',
        borderTop: '1px solid var(--gray-200)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        zIndex: 100,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {[
        { to: '/dashboard', icon: <LayoutDashboard size={20} />, label: 'Home' },
        { to: '/directory', icon: <Search size={20} />, label: 'Directory' },
        { to: '/agencies', icon: <Building2 size={20} />, label: 'Agencies' },
        { to: '/flags', icon: <Flag size={20} />, label: 'Raised Flags' },
      ].map((tab) => {
        const active = location.pathname === tab.to || location.pathname.startsWith(tab.to + '/');
        return (
          <Link
            key={tab.to}
            to={tab.to}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              flex: 1,
              padding: '6px 0',
              textDecoration: 'none',
              color: active ? 'var(--navy)' : 'var(--gray-500)',
              fontSize: 12,
              fontWeight: active ? 700 : 400,
            }}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </Link>
        );
      })}
    </div>
  ) : null;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', paddingTop: 36, boxSizing: 'border-box' }}>
      {/* Desktop sidebar */}
      {!isMobile && sidebarContent}

      {/* Mobile sidebar overlay + slide */}
      {isMobile && (
        <>
          {sidebarOpen && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 199,
              }}
              onClick={() => setSidebarOpen(false)}
            />
          )}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              zIndex: 200,
              display: 'flex',
              transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.25s ease',
            }}
          >
            {sidebarContent}
          </div>
        </>
      )}

      {/* Main column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Topbar */}
        <header
          style={{
            height: 48,
            minHeight: 48,
            background: 'var(--white)',
            borderBottom: '1px solid var(--gray-200)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
            gap: 12,
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          {/* Mobile hamburger */}
          {isMobile && (
            <button
              aria-label="Open menu"
              onClick={() => setSidebarOpen(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                color: 'var(--gray-700)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Menu size={20} />
            </button>
          )}

          {/* Breadcrumb */}
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--gray-900)',
              flex: 1,
            }}
          >
            {breadcrumb}
          </span>

        </header>

        {/* Main content */}
        <main
          style={{
            flex: 1,
            overflow: 'auto',
            background: 'var(--bg)',
            paddingBottom: isMobile ? 56 : 0,
          }}
        >
          {children}
        </main>
      </div>

      {/* Mobile tab bar */}
      {mobileTabBar}

      {showAddContact && (
        <AddContactModal
          onClose={() => setShowAddContact(false)}
        />
      )}
    </div>
  );
};
